import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from '@/hooks/auth';
import { toast } from 'sonner';

interface Favorite {
  id?: string;
  user_id: string;
  item_id: string;
  item_type: string;
  metadata?: any;
  created_at?: string;
}

export function useUserFavorites(itemType?: string) {
  const { user } = useSupabaseAuth();
  const queryClient = useQueryClient();

  const { data: favorites = [], isLoading } = useQuery({
    queryKey: ['user-favorites', user?.id, itemType],
    queryFn: async () => {
      if (!user) return [];
      
      let query = supabase
        .from('user_favorites')
        .select('*')
        .eq('user_id', user.id);

      if (itemType) {
        query = query.eq('item_type', itemType);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const addFavorite = useMutation({
    mutationFn: async ({ itemId, itemType }: { itemId: string; itemType: string }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_favorites')
        .insert({
          user_id: user.id,
          item_id: itemId,
          item_type: itemType,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-favorites', user?.id] });
      toast.success('Added to favorites');
    },
    onError: (error) => {
      console.error('Failed to add favorite:', error);
      toast.error('Failed to add to favorites');
    },
  });

  const removeFavorite = useMutation({
    mutationFn: async (itemId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('item_id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-favorites', user?.id] });
      toast.success('Removed from favorites');
    },
    onError: (error) => {
      console.error('Failed to remove favorite:', error);
      toast.error('Failed to remove from favorites');
    },
  });

  const isFavorite = (itemId: string) => {
    return favorites.some(fav => fav.item_id === itemId);
  };

  return {
    favorites,
    isLoading,
    addFavorite: addFavorite.mutate,
    removeFavorite: removeFavorite.mutate,
    isFavorite,
    isAdding: addFavorite.isPending,
    isRemoving: removeFavorite.isPending,
  };
}
